import type {
  HasAverageColor,
  HasBackgroundColor,
  VirtualViewportPixelAccessor,
} from "../pixel-accessor";
import type { ReversePixelMapper } from "../pixel-mapper";
import type {
  ResampleFilterResolver,
  ResampleFilter,
  ResampleFilterPresetsPoolKeyMap,
  ResampleFilterPreset,
} from "../resample-filter";
import type { ColorResampler } from "./ColorResampler";
import type {
  ColorResamplerFactoriesPool,
  ColorResamplerFactoriesPoolKeyMap,
} from "./ColorResamplerFactoriesPool";
import type { ColorResamplerFactory } from "./ColorResamplerFactory";
import type {
  ColorResamplerResolver,
  ColorResamplerResolverOptions,
} from "./ColorResamplerResolver";

/**
 * Base implementation of ColorResamplerResolver interface.
 */
export class BaseColorResamplerResolver implements ColorResamplerResolver {
  /**
   * BaseColorResamplerResolver constructor.
   *
   * @param colorResamplerFactoriesPool
   * @param resampleFilterResolver
   */
  constructor(
    private readonly colorResamplerFactoriesPool: ColorResamplerFactoriesPool,
    private readonly resampleFilterResolver: ResampleFilterResolver
  ) {}

  /**
   * @inheritDoc
   */
  resolve(
    image: VirtualViewportPixelAccessor & HasAverageColor & HasBackgroundColor,
    reversePixelMapper: ReversePixelMapper,
    options: ColorResamplerResolverOptions = {}
  ): ColorResampler {
    const {
      preferredResampler,
      filter,
      matteColor,
      interpolationMethod,
      virtualPixelMethod,
    } = options;
    const factory = this.getColorResamplerFactory(preferredResampler, filter);
    let resampleFilter: ResampleFilter | undefined;
    if (filter && factory.supportsEwa) {
      resampleFilter = this.resampleFilterResolver.resolve(filter, options);
    }
    return factory.create(image, reversePixelMapper, {
      resampleFilter,
      matteColor,
      interpolationMethod,
      virtualPixelMethod,
    });
  }

  /**
   * Returns matching color resampler factory.
   *
   * @param preferredResampler
   * @param filter
   * @private
   */
  private getColorResamplerFactory(
    preferredResampler?: keyof ColorResamplerFactoriesPoolKeyMap | string,
    filter?:
      | keyof ResampleFilterPresetsPoolKeyMap
      | ResampleFilterPreset
      | ResampleFilter
  ): ColorResamplerFactory {
    let factory: ColorResamplerFactory | undefined;
    if (preferredResampler !== undefined) {
      const factory = this.colorResamplerFactoriesPool.get(preferredResampler);
      if (factory.requiresResampleFilter && !filter) {
        throw new TypeError(
          `Preferred color resampler "${preferredResampler}" requires ResampleFilter`
        );
      }
      return factory;
    } else if (filter !== undefined) {
      factory = this.colorResamplerFactoriesPool
        .items()
        .find((f) => f.supportsEwa);
      if (factory) {
        return factory;
      }
    }
    const factories = this.colorResamplerFactoriesPool
      .items()
      .filter((f) => !f.requiresResampleFilter);
    if (!factories.length) {
      throw new Error("No color resampler factories in pool.");
    }
    return factories[0];
  }
}
